import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, Modal, Descriptions, Card, DatePicker, Select, Row, Col, message, Input } from 'antd';
import { EyeOutlined, SearchOutlined, CheckOutlined, CarOutlined, CheckCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const RequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<any>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRequestId, setCancelRequestId] = useState<string | null>(null);

  useEffect(() => {
    queueMicrotask(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    });
  }, [filters]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['requests', filters, pagination],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const offset = (pagination.current - 1) * pagination.pageSize;
      params.append('limit', String(pagination.pageSize));
      params.append('offset', String(offset));
      const response = await api.get(`/admin/service-requests?${params}`);
      return response.data;
    }
  });

  const acceptMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/accept`);
      return response.data;
    },
    onSuccess: () => {
      message.success(t('requests.accepted'));
      refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || t('requests.acceptError');
      message.error(errorMessage);
    }
  });

  const deliverMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/deliver`);
      return response.data;
    },
    onSuccess: () => {
      message.success(t('requests.delivered'));
      refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || t('requests.deliverError');
      message.error(errorMessage);
    }
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/service-requests/${id}/complete`);
      return response.data;
    },
    onSuccess: () => {
      message.success(t('requests.completed'));
      refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || t('requests.completeError');
      message.error(errorMessage);
    }
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/service-requests/${id}/cancel`, { reason });
      return response.data;
    },
    onSuccess: () => {
      message.success(t('requests.cancelled'));
      setCancelModalVisible(false);
      setCancelReason('');
      setCancelRequestId(null);
      refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || t('requests.cancelError');
      message.error(errorMessage);
    }
  });

  const statusColors: Record<string, string> = {
    pending: 'default',
    accepted: 'processing',
    delivered: 'success',
    completed: 'green',
    cancelled: 'error',
    expired: 'warning',
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => id.substring(0, 8),
    },
    {
      title: t('requests.car'),
      key: 'car',
      render: (_: any, record: any) => `${record.car?.make} ${record.car?.model} (${record.car?.licensePlate})`,
    },
    {
      title: t('requests.client'),
      key: 'client',
      render: (_: any, record: any) => `${record.client?.firstName} ${record.client?.lastName}`,
    },
    {
      title: t('requests.valet'),
      key: 'valet',
      render: (_: any, record: any) => record.valet ? `${record.valet.firstName} ${record.valet.lastName}` : '-',
    },
    {
      title: t('requests.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={statusColors[status]}>{t(`requests.${status}`)}</Tag>,
    },
    {
      title: t('requests.createdAt'),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      render: (date: any) => {
        if (!date) return '-';
        const d = date instanceof Date ? date : new Date(date);
        return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
      },
    },
    {
      title: t('requests.completionTime'),
      dataIndex: 'actualPickupAt',
      key: 'completionTime',
      render: (_: any, record: any) => {
        if (!record.actualPickupAt || !record.requestedAt) return '-';
        const d1 = record.requestedAt instanceof Date ? record.requestedAt : new Date(record.requestedAt);
        const d2 = record.actualPickupAt instanceof Date ? record.actualPickupAt : new Date(record.actualPickupAt);
        const diff = d2.getTime() - d1.getTime();
        return `${Math.round(diff / 60000)} min`;
      },
    },
    {
      title: t('requests.actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          {record.status === 'pending' && (
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />}
              onClick={() => acceptMutation.mutate(record.id)}
              loading={acceptMutation.isPending}
              title={t('requests.accept')}
            />
          )}
          {record.status === 'accepted' && (
            <Button 
              type="primary" 
              size="small" 
              icon={<CarOutlined />}
              onClick={() => deliverMutation.mutate(record.id)}
              loading={deliverMutation.isPending}
              title={t('requests.delivered')}
            />
          )}
          {record.status === 'delivered' && (
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckCircleOutlined />}
              onClick={() => completeMutation.mutate(record.id)}
              loading={completeMutation.isPending}
              title={t('requests.completed')}
            />
          )}
          {['pending', 'accepted', 'delivered'].includes(record.status) && (
            <Button 
              danger 
              size="small" 
              icon={<CloseOutlined />}
              onClick={() => { setCancelRequestId(record.id); setCancelModalVisible(true); }}
              title={t('requests.cancel')}
            />
          )}
          <Button icon={<EyeOutlined />} size="small" onClick={() => { setSelectedRequest(record); setDetailsVisible(true); }} title={t('requests.details')} />
        </Space>
      ),
    },
  ];

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      setFilters((prev: any) => ({ ...prev, startDate: dates[0].toISOString(), endDate: dates[1].toISOString() }));
    } else {
      setFilters((prev: any) => ({ ...prev, startDate: null, endDate: null }));
    }
  };

  return (
    <div>
      <h1>{t('requests.title')}</h1>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col>
            <Space>
              <Select
                placeholder={t('requests.status')}
                allowClear
                style={{ width: 150 }}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Option value="pending">{t('requests.pending')}</Option>
                <Option value="accepted">{t('requests.accepted')}</Option>
                <Option value="delivered">{t('requests.delivered')}</Option>
                <Option value="completed">{t('requests.completed')}</Option>
                <Option value="cancelled">{t('requests.cancelled')}</Option>
              </Select>
              <RangePicker 
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                placeholder={[t('common.from'), t('common.to')]}
              />
              <Button icon={<SearchOutlined />} onClick={() => refetch()}>{t('requests.filter')}</Button>
            </Space>
          </Col>
        </Row>
      </Card>
      <Table
        dataSource={data?.requests || []}
        columns={columns}
        rowKey="id"
        loading={{ spinning: isLoading, tip: t('common.loading') }}
        locale={{
          emptyText: t('common.noData'),
        }}
        pagination={{
          ...pagination,
          total: data?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number) => `${t('common.total')}: ${total}`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
      />
      <Modal
        title={t('requests.details')}
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRequest && (
          <Descriptions 
            bordered 
            size="small" 
            column={1}
            labelStyle={{ padding: '4px 8px' }}
            contentStyle={{ padding: '4px 8px' }}
          >
            <Descriptions.Item label="ID">{selectedRequest.id}</Descriptions.Item>
            <Descriptions.Item label={t('requests.status')}>
              <Tag color={statusColors[selectedRequest.status]}>{t(`requests.${selectedRequest.status}`)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('requests.car')}>
              {selectedRequest.car?.make} {selectedRequest.car?.model} ({selectedRequest.car?.licensePlate})
            </Descriptions.Item>
            <Descriptions.Item label={t('requests.client')}>
              {selectedRequest.client?.firstName} {selectedRequest.client?.lastName}
              <br />
              {selectedRequest.client?.email}
            </Descriptions.Item>
            <Descriptions.Item label={t('requests.valet')}>
              {selectedRequest.valet ? `${selectedRequest.valet.firstName} ${selectedRequest.valet.lastName}` : t('requests.unassigned')}
            </Descriptions.Item>
            <Descriptions.Item label={t('requests.parkingSpot')}>{selectedRequest.parkingSpot || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('requests.estimatedTime')}>{selectedRequest.estimatedTimeMinutes || '-'} min</Descriptions.Item>
            <Descriptions.Item label={t('requests.createdAt')}>{
              (() => {
                if (!selectedRequest.requestedAt) return '-';
                const d = selectedRequest.requestedAt instanceof Date ? selectedRequest.requestedAt : new Date(selectedRequest.requestedAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.updatedAt')}>{
              (() => {
                if (!selectedRequest.updatedAt) return '-';
                const d = selectedRequest.updatedAt instanceof Date ? selectedRequest.updatedAt : new Date(selectedRequest.updatedAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.acceptedAt')}>{
              (() => {
                if (!selectedRequest.acceptedAt) return '-';
                const d = selectedRequest.acceptedAt instanceof Date ? selectedRequest.acceptedAt : new Date(selectedRequest.acceptedAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.deliveredAt')}>{
              (() => {
                if (!selectedRequest.deliveredAt) return '-';
                const d = selectedRequest.deliveredAt instanceof Date ? selectedRequest.deliveredAt : new Date(selectedRequest.deliveredAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.deliveryTime')}>{
              (() => {
                if (!selectedRequest.acceptedAt || !selectedRequest.deliveredAt) return '-';
                const d1 = new Date(selectedRequest.acceptedAt).getTime();
                const d2 = new Date(selectedRequest.deliveredAt).getTime();
                const diff = d2 - d1;
                return `${Math.round(diff / 60000)} min`;
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.pickupDeadline')}>{
              (() => {
                if (!selectedRequest.pickupDeadlineAt) return '-';
                const d = selectedRequest.pickupDeadlineAt instanceof Date ? selectedRequest.pickupDeadlineAt : new Date(selectedRequest.pickupDeadlineAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.completedAt')}>{
              (() => {
                if (!selectedRequest.actualPickupAt) return '-';
                const d = selectedRequest.actualPickupAt instanceof Date ? selectedRequest.actualPickupAt : new Date(selectedRequest.actualPickupAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.cancelledAt')}>{
              (() => {
                if (!selectedRequest.cancelledAt) return '-';
                const d = selectedRequest.cancelledAt instanceof Date ? selectedRequest.cancelledAt : new Date(selectedRequest.cancelledAt);
                return isNaN(d.getTime()) ? '-' : d.toLocaleString('pl-PL');
              })()
            }</Descriptions.Item>
            <Descriptions.Item label={t('requests.cancellationReason')}>
              {selectedRequest.cancellationReason || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={t('requests.cancel')}
        open={cancelModalVisible}
        onOk={() => cancelMutation.mutate({ id: cancelRequestId!, reason: cancelReason })}
        onCancel={() => { setCancelModalVisible(false); setCancelReason(''); setCancelRequestId(null); }}
        okText={t('common.confirm')}
        okButtonProps={{ danger: true }}
        confirmLoading={cancelMutation.isPending}
      >
        <p>{t('requests.cancelReasonPrompt')}</p>
        <TextArea 
          rows={4} 
          value={cancelReason} 
          onChange={(e) => setCancelReason(e.target.value)} 
          placeholder={t('requests.cancellationReasonPlaceholder')}
        />
      </Modal>
    </div>
  );
};

export default RequestsPage;
